import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Divider, Heading } from '@chakra-ui/react';

import { Markdown } from '~/components/markdown';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { EnterpriseResourceContainer } from '~/features/enterprise-resource/components/enterprise-resource-container';
import { EnterpriseResourceForm } from '~/features/enterprise-resource/components/enterprise-resource-form';
import { EnterpriseResourceHero } from '~/features/enterprise-resource/components/enterprise-resource-hero';
import { EnterpriseResourceHeroWithLeadForm } from '~/features/enterprise-resource/components/enterprise-resource-hero-with-lead-form';
import { EnterpriseResourcePerson } from '~/features/enterprise-resource/components/enterprise-resource-person';
import { getEnterpriseWebinarContent } from '~/features/enterprise-resource/controllers/get-enterprise-webinar-content';
import { EnterpriseWebinarContent } from '~/features/enterprise-resource/models/enterprise-webinar-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

type EnterpriseWebinarPageProps = {
  page: Page;
  enterpriseWebinarContent: EnterpriseWebinarContent;
};

export default function EnterpriseWebinarPage({ page, enterpriseWebinarContent }: EnterpriseWebinarPageProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const webinarData = enterpriseWebinarContent.webinarData;
  const startTime = webinarData?.startTimes?.map((startTime) => startTime.time).join(' & ');
  const formData = webinarData.form;

  function handlePrimaryCtaClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    trackEvent({
      type: 'click',
      name: 'Enterprise Webinar CTA Clicked',
      label: 'shapes_banner_cta_clicked',
      value: enterpriseWebinarContent.slug,
    });
  }

  const FormSlotContent = webinarData.isGated && formData && (
    <EnterpriseResourceForm
      ref={formRef}
      additionalEventProperties={{ webinarName: enterpriseWebinarContent.slug }}
      enableResponseModal={true}
      leadCaptureType={webinarData.leadCaptureType}
      resourceType='webinar'
      startDate={webinarData.startDate}
      startTimes={webinarData.startTimes ?? undefined}
      templateForm={formData}
      errorModalProps={{
        title: 'Registration Error',
        message: 'There was an issue registering for this event. Please try again later.',
      }}
      successModalProps={{
        title: 'Registration Successful',
        message: `You are now registered for the "${webinarData.title}" webinar.`,
      }}
    />
  );

  const PersonListContent = (
    <Box
      columnGap='24px'
      display='flex'
      flexWrap='wrap'
      marginTop={{ base: '40px', md: '64px', lg: '120px' }}
      paddingRight={{ base: '0px', lg: formData ? '32px' : '0px' }}
      rowGap='40px'
    >
      {webinarData.personList.map((person) => (
        <Box key={person.name} flexBasis={{ base: '100%', md: 'calc(50% - 12px)' }}>
          <EnterpriseResourcePerson person={person} />
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.EnterpriseWebinar} />
      <PageLayout overflowX='hidden' withoutVerticalPadding>
        {webinarData.isGated && (
          <EnterpriseResourceHeroWithLeadForm
            contentSlot={PersonListContent}
            description={webinarData.description}
            formSlot={FormSlotContent}
            relatedCompanies={webinarData.relatedCompanies}
            resourceTypeTitle={webinarData.resourceTypeTitle}
            startDate={webinarData.startDate}
            startTime={startTime}
            title={webinarData.title}
          />
        )}

        {!webinarData.isGated && webinarData.heroImage && webinarData.primaryCta && (
          <>
            <EnterpriseResourceHero
              description={webinarData.description}
              image={webinarData.heroImage}
              primaryCta={webinarData.primaryCta}
              relatedCompanies={webinarData.relatedCompanies}
              resourceTypeTitle={webinarData.resourceTypeTitle}
              startDate={webinarData.startDate}
              startTime={startTime}
              title={webinarData.title}
            />

            <EnterpriseResourceContainer>{PersonListContent}</EnterpriseResourceContainer>
          </>
        )}

        <EnterpriseResourceContainer marginBottom={{ base: '80px', md: '120px' }}>
          <Divider marginY={{ base: '40px', md: '64px', lg: '80px' }} />

          <Box display='flex' flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
            <Heading
              as='h2'
              marginBottom={{ base: '40px', lg: '0px' }}
              marginRight={{ base: '0px', lg: '120px' }}
              size='marketing-4xl'
            >
              {webinarData.contentTitle}
            </Heading>

            <Markdown markdownText={webinarData.contentDescription} />
          </Box>
        </EnterpriseResourceContainer>

        {webinarData.shapesBanner && (
          <ShapesBanner
            {...webinarData.shapesBanner}
            flipTitleAndSubtitleOrder={true}
            onPrimaryCtaClick={handlePrimaryCtaClick}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ slug: string }>): Promise<GetStaticPropsResult<EnterpriseWebinarPageProps>> {
  const slug = params?.slug;

  if (!slug) {
    logger.fatal('Enterprise resource slug not found', {
      file: 'src/pages/webinar/[slug].tsx',
      function: 'getStaticProps',
      slug,
    });

    throw new Error('Enterprise resource slug not found');
  }

  const [page, enterpriseWebinarContent] = await Promise.all([getPage('/webinar'), getEnterpriseWebinarContent(slug)]);

  if (!enterpriseWebinarContent) {
    return {
      notFound: true,
    };
  }

  // If enterpriseWebinarContent has seo, override default page seo
  if (enterpriseWebinarContent.seo) {
    page.seo = enterpriseWebinarContent.seo;
  }

  return {
    props: {
      page,
      enterpriseWebinarContent,
    },
  };
}
